export interface Verse {
  id: number;
  arabic: string;
  transliteration: string;
  translation: string;
  startTime?: number; // Seconds
  endTime?: number;
}

export interface Surah {
  id: number;
  name: string;
  englishName: string;
  meaning: string;
  verses: Verse[];
  audioUrl?: string; // Full Surah audio or per verse logic later
}

export const quranData: Record<number, Surah> = {
  1: {
    id: 1,
    name: "Al-Fatiha",
    englishName: "The Opening",
    meaning: "The Opening",
    audioUrl: "https://server8.mp3quran.net/afs/001.mp3", // Mishary Rashid Alafasy
    verses: [
      {
        id: 1,
        arabic: "بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ",
        transliteration: "Bismillāhi r-raḥmāni r-raḥīm",
        translation: "In the name of Allah, the Entirely Merciful, the Especially Merciful.",
        startTime: 0, endTime: 11 // Covers Isti'adhah + Bismillah
      },
      {
        id: 2,
        arabic: "ٱلْحَمْدُ لِلَّهِ رَبِّ ٱلْعَـٰلَمِينَ",
        transliteration: "Al-ḥamdu lillāhi rabbi l-ʿālamīn",
        translation: "[All] praise is [due] to Allah, Lord of the worlds.",
        startTime: 11, endTime: 16 // Al-Hamd...
      },
      {
        id: 3,
        arabic: "ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ",
        transliteration: "Ar-raḥmāni r-raḥīm",
        translation: "The Entirely Merciful, the Especially Merciful.",
        startTime: 16, endTime: 20 // Ar-Rahman... (Extended +1s)
      },
      {
        id: 4,
        arabic: "مَـٰلِكِ يَوْمِ ٱلدِّينِ",
        transliteration: "Māliki yawmi d-dīn",
        translation: "Sovereign of the Day of Recompense.",
        startTime: 20, endTime: 24 // Maliki... (Shifted +1s)
      },
      {
        id: 5,
        arabic: "إِيَّاكَ نَعْبُدُ وَإِيَّاكَ نَسْتَعِينُ",
        transliteration: "Iyyāka naʿbudu wa-iyyāka nastaʿīn",
        translation: "It is You we worship and You we ask for help.",
        startTime: 24, endTime: 29 // Iyyaka... (Shifted +1s)
      },
      {
        id: 6,
        arabic: "ٱهْدِنَا ٱلصِّرَٰطَ ٱلْمُسْتَقِيمَ",
        transliteration: "Ihdinā ṣ-ṣirāṭa l-mustaqīm",
        translation: "Guide us to the straight path.",
        startTime: 29, endTime: 34 // Ihdina... (Shifted +1s)
      },
      {
        id: 7,
        arabic: "صِرَٰطَ ٱلَّذِينَ أَنْعَمْتَ عَلَيْهِمْ غَيْرِ ٱلْمَغْضُوبِ عَلَيْهِمْ وَلَا ٱلضَّآلِّينَ",
        transliteration: "Ṣirāṭa lladhīna anʿamta ʿalayhim ghayri l-maghḍūbi ʿalayhim wala ḍ-ḍāllīn",
        translation: "The path of those upon whom You have bestowed favor, not of those who have evoked [Your] anger or of those who are astray.",
        startTime: 34, endTime: 51 // Sirata... (Shifted +1s)
      }
    ]
  },
  112: {
    id: 112,
    name: "Al-Ikhlas",
    englishName: "The Sincerity",
    meaning: "The Sincerity",
    audioUrl: "https://server8.mp3quran.net/afs/112.mp3",
    verses: [
      {
        id: 1,
        arabic: "قُلْ هُوَ ٱللَّهُ أَحَدٌ",
        transliteration: "Qul huwa llāhu aḥad",
        translation: "Say, 'He is Allah, [who is] One.'",
        startTime: 5, endTime: 9 // Bismillah (0-5) -> Qul (5-9)
      },
      {
        id: 2,
        arabic: "ٱللَّهُ ٱلصَّمَدُ",
        transliteration: "Allāhu ṣ-ṣamad",
        translation: "Allah, the Eternal Refuge.",
        startTime: 9, endTime: 13
      },
      {
        id: 3,
        arabic: "لَمْ يَلِدْ وَلَمْ يُولَدْ",
        transliteration: "Lam yalid wa-lam yūlad",
        translation: "He neither begets nor is born,",
        startTime: 13, endTime: 17
      },
      {
        id: 4,
        arabic: "وَلَمْ يَكُن لَّهُۥ كُفُوًا أَحَدٌۢ",
        transliteration: "Wa-lam yakun lahū kufuwan aḥad",
        translation: "Nor is there to Him any equivalent.'",
        startTime: 17, endTime: 22
      }
    ]
  },
  113: {
    id: 113,
    name: "Al-Falaq",
    englishName: "The Daybreak",
    meaning: "The Daybreak",
    audioUrl: "https://server8.mp3quran.net/afs/113.mp3",
    verses: [
      {
        id: 1,
        arabic: "قُلْ أَعُوذُ بِرَبِّ ٱلْفَلَقِ",
        transliteration: "Qul aʿūdhu bi-rabbi l-falaq",
        translation: "Say, 'I seek refuge in the Lord of daybreak"
      },
      {
        id: 2,
        arabic: "مِن شَرِّ مَا خَلَقَ",
        transliteration: "Min sharri mā khalaq",
        translation: "From the evil of that which He created"
      },
      {
        id: 3,
        arabic: "وَمِن شَرِّ غَاسِقٍ إِذَا وَقَبَ",
        transliteration: "Wa-min sharri ghāsiqin idhā waqab",
        translation: "And from the evil of darkness when it settles"
      },
      {
        id: 4,
        arabic: "وَمِن شَرِّ ٱلنَّفَّـٰثَـٰتِ فِى ٱلْعُقَدِ",
        transliteration: "Wa-min sharri n-naffāthāti fi l-ʿuqad",
        translation: "And from the evil of the blowers in knots"
      },
      {
        id: 5,
        arabic: "وَمِن شَرِّ حَاسِدٍ إِذَا حَسَدَ",
        transliteration: "Wa-min sharri ḥāsidin idhā ḥasad",
        translation: "And from the evil of an envier when he envies.'"
      }
    ]
  },
  114: {
    id: 114,
    name: "An-Nas",
    englishName: "The Mankind",
    meaning: "The Mankind",
    audioUrl: "https://server8.mp3quran.net/afs/114.mp3",
    verses: [
      {
        id: 1,
        arabic: "قُلْ أَعُوذُ بِرَبِّ ٱلنَّاسِ",
        transliteration: "Qul aʿūdhu bi-rabbi n-nās",
        translation: "Say, 'I seek refuge in the Lord of mankind,"
      },
      {
        id: 2,
        arabic: "مَلِكِ ٱلنَّاسِ",
        transliteration: "Maliki n-nās",
        translation: "The Sovereign of mankind,"
      },
      {
        id: 3,
        arabic: "إِلَـٰهِ ٱلنَّاسِ",
        transliteration: "Ilāhi n-nās",
        translation: "The God of mankind,"
      },
      {
        id: 4,
        arabic: "مِن شَرِّ ٱلْوَسْوَاسِ ٱلْخَنَّاسِ",
        transliteration: "Min sharri l-waswāsi l-khannās",
        translation: "From the evil of the retreating whisperer -"
      },
      {
        id: 5,
        arabic: "ٱلَّذِى يُوَسْوِسُ فِى صُدُورِ ٱلنَّاسِ",
        transliteration: "Alladhī yuwaswisu fī ṣudūri n-nās",
        translation: "Who whispers [evil] into the breasts of mankind -"
      },
      {
        id: 6,
        arabic: "مِنَ ٱلْجِنَّةِ وَٱلنَّاسِ",
        transliteration: "Mina l-jinnati wa-n-nās",
        translation: "From among the jinn and mankind.'"
      }
    ]
  },
  110: {
    id: 110,
    name: "An-Nasr",
    englishName: "The Divine Support",
    meaning: "The Divine Support",
    audioUrl: "https://server8.mp3quran.net/afs/110.mp3",
    verses: [
      {
        id: 1,
        arabic: "إِذَا جَآءَ نَصْرُ ٱللَّهِ وَٱلْفَتْحُ",
        transliteration: "Idhā jāʾa naṣru llāhi wa-l-fatḥ",
        translation: "When the victory of Allah has come and the conquest,"
      },
      {
        id: 2,
        arabic: "وَرَأَيْتَ ٱلنَّاسَ يَدْخُلُونَ فِى دِينِ ٱللَّهِ أَفْوَاجًا",
        transliteration: "Wa-raʾayta n-nāsa yadkhulūna fī dīni llāhi afwājā",
        translation: "And you see the people entering into the religion of Allah in multitudes,"
      },
      {
        id: 3,
        arabic: "فَسَبِّحْ بِحَمْدِ رَبِّكَ وَٱسْتَغْفِرْهُ ۚ إِنَّهُۥ كَانَ تَوَّابًۢا",
        transliteration: "Fa-sabbiḥ bi-ḥamdi rabbika wa-staghfirhu innahū kāna tawwābā",
        translation: "Then exalt [Him] with praise of your Lord and ask forgiveness of Him. Indeed, He is ever Accepting of repentance."
      }
    ]
  },
  111: {
    id: 111,
    name: "Al-Masad",
    englishName: "The Palm Fiber",
    meaning: "The Palm Fiber",
    audioUrl: "https://server8.mp3quran.net/afs/111.mp3",
    verses: [
      {
        id: 1,
        arabic: "تَبَّتْ يَدَآ أَبِى لَهَبٍ وَتَبَّ",
        transliteration: "Tabbat yadā abī lahabin wa-tabb",
        translation: "May the hands of Abu Lahab be ruined, and ruined is he."
      },
      {
        id: 2,
        arabic: "مَآ أَغْنَىٰ عَنْهُ مَالُهُۥ وَمَا كَسَبَ",
        transliteration: "Mā aghnā ʿanhu māluhū wa-mā kasab",
        translation: "His wealth will not avail him or that which he gained."
      },
      {
        id: 3,
        arabic: "سَيَصْلَىٰ نَارًا ذَاتَ لَهَبٍ",
        transliteration: "Sa-yaṣlā nāran dhāta lahab",
        translation: "He will [enter to] burn in a Fire of [blazing] flame"
      },
      {
        id: 4,
        arabic: "وَٱمْرَأَتُهُۥ حَمَّالَةَ ٱلْحَطَبِ",
        transliteration: "Wa-mraʾatuhū ḥammālata l-ḥaṭab",
        translation: "And his wife [as well] - the carrier of firewood."
      },
      {
        id: 5,
        arabic: "فِى جِيدِهَا حَبْلٌ مِّن مَّسَدٍۢ",
        transliteration: "Fī jīdihā ḥablun min masad",
        translation: "Around her neck is a rope of [twisted] fiber."
      }
    ]
  },
  109: {
    id: 109,
    name: "Al-Kafirun",
    englishName: "The Disbelievers",
    meaning: "The Disbelievers",
    audioUrl: "https://server8.mp3quran.net/afs/109.mp3",
    verses: [
      {
        id: 1,
        arabic: "قُلْ يَـٰٓأَيُّهَا ٱلْكَـٰفِرُونَ",
        transliteration: "Qul yā-ayyuhā l-kāfirūn",
        translation: "Say, 'O disbelievers,"
      },
      {
        id: 2,
        arabic: "لَآ أَعْبُدُ مَا تَعْبُدُونَ",
        transliteration: "Lā aʿbudu mā taʿbudūn",
        translation: "I do not worship what you worship."
      },
      {
        id: 3,
        arabic: "وَلَآ أَنتُمْ عَـٰبِدُونَ مَآ أَعْبُدُ",
        transliteration: "Wa-lā antum ʿābidūna mā aʿbud",
        translation: "Nor are you worshippers of what I worship."
      },
      {
        id: 4,
        arabic: "وَلَآ أَنَا۠ عَـابِدٌ مَّا عَبَدتُّمْ",
        transliteration: "Wa-lā ana ʿābidun mā ʿabattum",
        translation: "Nor will I be a worshipper of what you worship."
      },
      {
        id: 5,
        arabic: "وَلَآ أَنتُمْ عَـٰبِدُونَ مَآ أَعْبُدُ",
        transliteration: "Wa-lā antum ʿābidūna mā aʿbud",
        translation: "Nor will you be worshippers of what I worship."
      },
      {
        id: 6,
        arabic: "لَكُمْ دِينُكُمْ وَلِىَ دِينِ",
        transliteration: "Lakum dīnukum wa-liya dīn",
        translation: "For you is your religion, and for me is my religion.'"
      }
    ]
  }
};

// Map Days to Surah IDs for the "Daily Quest"
export const dailyPlan: number[] = [
    1,   // Day 1: Al-Fatiha
    112, // Day 2: Al-Ikhlas
    113, // Day 3: Al-Falaq
    114, // Day 4: An-Nas
    109, // Day 5: Al-Kafirun
    110, // Day 6: An-Nasr
    111  // Day 7: Al-Masad
];
